<template>
  <section class="search-results">
    <div class="container" v-if="!searchResultsAvailable">
      <div class="loading-data">
        <span>Trwa wyszukiwanie połączeń, proszę czekać...</span>
      </div>
    </div>
    <div class="container" v-if="searchResultsAvailable">
      <div class="search-title">
        <span class="keyword">Połączenia</span>
        <div class="stations">
          <span class="station-from">{{ stationFrom.displayName }}</span>
          <span class="station-to">{{ stationTo.displayName }}</span>
        </div>
      </div>
      <div class="results-table">
        <div class="header">
          <div class="departure-time">
            <span class="single">Czas odjazdu</span>
          </div>
          <div class="arrival-time">
            <span class="single">Czas przyjazdu</span>
          </div>
          <div class="route-info">
            <span class="multi">Rodzaj połączenia</span>
            <span class="multi">Numer pociągu</span>
          </div>
          <div class="travel-time">
            <span class="single">Czas podróży</span>
          </div>
          <div class="price">
            <span class="single">Cena biletu</span>
          </div>
          <div class="small-space"></div>
        </div>
        <div class="body">
          <div class="no-data" v-if="noData">
            <span>Brak wyników, spróbuj wyszukać z innymi parametrami.</span>
          </div>
          <div v-if="!noData">
            <div class="single-row">
              <a class="more-results results-before" @click="fetchMore(false)">Wcześniej</a>
            </div>
            <div class="results">
              <div class="day-group" v-for="(data, day) in this.resultsByDate" :key="day">
                <div class="single-row date-info-ctr">
                  <span class="date-info">{{ day }}</span>
                </div>
                <div class="result-item" v-for="(item, index) in data" :key="index">
                  <div class="departure-time">
                    <span class="single">{{ formatTime(item.departureTime) }}</span>
                  </div>
                  <div class="arrival-time">
                    <span class="single">{{ formatTime(item.arrivalTime) }}</span>
                  </div>
                  <div class="route-info">
                    <TrainCategory
                      :selectable="false"
                      :category="getTrainCategory(item.routeInfo.trainCatID)"
                    />
                    <span class="multi">{{ item.routeInfo.trainID }}</span>
                  </div>
                  <div class="travel-time">
                    <span class="single">{{ formatTravelTime(item.travelTime) }}</span>
                  </div>
                  <div class="price">
                    <span class="single">{{ formatPrice(item.price) }}</span>
                  </div>
                  <div class="small-space">
                    <button
                      v-if="item.purchasable"
                      type="button"
                      class="buy-button upper"
                      title="Kliknij, aby dokonać zakupu biletu"
                      @click="buyTicket(item)"
                    >
                      <i class="fas fa-credit-card"></i>ZAKUP
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="single-row">
              <a class="more-results results-after" @click="fetchMore(true)">Później</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { isNull } from 'util';
import TrainCategory from '../controls/TrainCategory.vue';

import storeMixins from '../mixins/storeMixins';
import findMixins from '../mixins/findMixins';

import updatePageTitle from '../helpers/updatePageTitle';

import moment from '../helpers/moment';

export default {
  name: 'SearchResults',
  components: {
    TrainCategory,
  },
  mixins: [storeMixins, findMixins],
  props: {
    from: {
      type: String,
    },
    to: {
      type: String,
    },
    date: {
      type: String,
    },
    time: {
      type: String,
    },
    departure: {
      type: String,
    },
    categories: {
      type: String,
    },
  },
  data() {
    return {};
  },
  computed: {
    stationFrom() {
      return this.stations.find(
        station => station.id === this.searchResults.params.stationFromID,
      );
    },
    stationTo() {
      return this.stations.find(
        station => station.id === this.searchResults.params.stationToID,
      );
    },
    resultsByDate() {
      const result = {};

      this.searchResults.results.forEach((item) => {
        const date = moment(item.departureTime).format('dddd, Do MMMM');

        if (date in result) {
          result[date].push(item);
        } else {
          result[date] = [item];
        }
      });

      return result;
    },
    searchResultsAvailable() {
      return this.$store.getters['search/searchResultsAvailable'];
    },
    noData() {
      return (
        this.searchResultsAvailable && this.searchResults.results.length === 0
      );
    },
  },
  created() {
    // wywoływane, gdy wejście do aplikacji następuje za pomocą linku
    if (!this.$store.state.search.isSearching) {
      this.decodeSearchParams(this);
      this.$store.dispatch('search/searchTrains');
      this.updateTitle();
    }
  },
  beforeRouteUpdate(to, from, next) {
    // obsługa przechodzenia po historii w przeglądarce
    this.decodeSearchParams(to.params);
    this.$store.dispatch('search/searchTrains');

    this.updateTitle();

    next();
  },
  methods: {
    formatTime(input) {
      return moment(input).format('HH:mm');
    },
    formatTravelTime(minutes) {
      const hours = Math.trunc(minutes / 60);
      let min = minutes % 60;
      min = min < 10 ? `0${min}` : min;

      return `${hours}h${min}`;
    },
    formatPrice(value) {
      return `${value.toLocaleString('pl')} zł`;
    },
    stationIDFromName(name) {
      const found = this.stations.find(st => st.name === name);
      return found === undefined ? null : found.id;
    },
    decodeTrainCategories(data) {
      const names = data.split(',');
      return names
        .map(name => this.trainCategoryFromName(name))
        .filter(category => !isNull(category))
        .map(category => category.id);
    },
    decodeSearchParams(params) {
      // konwersja i zapisanie danych
      const data = {
        stationFromID: this.stationIDFromName(params.from),
        stationToID: this.stationIDFromName(params.to),
        date: params.date,
        time: params.time,
        departure: params.departure === 'true',
        trainCategories: this.decodeTrainCategories(params.categories),
      };

      this.$store.commit('search/setParams', data);
    },
    updateTitle() {
      const from = this.getStationById(this.searchParams.stationFromID)
        .displayName;
      const to = this.getStationById(this.searchParams.stationToID).displayName;

      updatePageTitle(`${from} > ${to}  | Lista połączeń`);
    },
    buyTicket(item) {
      if (!this.userSignedIn) {
        this.$router.push({ name: 'SignPage' });
        return;
      }

      this.$store.dispatch('myTickets/buyTicket', {
        token: this.userToken,
        entryID: item.id,
        stationFromID: this.searchParams.stationFromID,
        stationToID: this.searchParams.stationToID,
        onSuccess: () => {
          this.$router.push({ name: 'TicketsPage' });
        },
        onFailure: () => {
          // eslint-disable-next-line no-alert
          alert('Wystąpił błąd podczas zakupu tego biletu.');
        },
      });
    },
    fetchMore(later) {
      this.$store.dispatch('search/fetchMore', later);
    },
  },
};
</script>

<style scoped>
.loading-data {
  text-align: center;
  margin-top: 70px;
  font-size: 22px;
  font-weight: 300;
}

.search-results {
  margin: 50px 0;
  min-height: 600px;
}

.search-title {
  display: flex;
  font-size: 35px;
}

.search-title .keyword {
  font-weight: 900;
}

.search-title .stations {
  padding-left: 50px;
  font-weight: 300;
}

.search-title .stations .station-from::after {
  content: '\f054';
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  padding: 0 10px;
}

.results-table {
  margin-top: 20px;
}

.results-table .header {
  display: flex;
  flex-flow: row nowrap;
  background-color: rgba(63, 81, 181, 0.1);
  border-bottom: 2px solid var(--color-main);
}

.results-table .header div {
  font-size: 20px;
}

.results-table .header > div,
.results-table .body .results .result-item > div {
  width: calc(90% / 5);
  display: flex;
  flex-flow: column wrap;
  justify-content: center;
  align-items: center;
  padding: 5px 0;
}

.results-table .header .route-info span {
  font-size: 15px;
  line-height: 20px;
}

.results-table .small-space {
  width: 10% !important;
}

.results-table .body .no-data {
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  padding: 40px 10px;
  font-size: 22px;
  font-weight: 300;
}

.results-table .body .more-results {
  font-size: 15px;
  font-weight: 300;
  display: inline-block;
  padding: 5px 20px;
}

.results-table .body .more-results:hover {
  color: var(--color-main);
}

.results-table .body .more-results:before {
  font-family: 'Font Awesome 5 Free';
  font-weight: 900;
  padding-right: 5px;
}

.results-table .body .more-results.results-before::before {
  content: '\f100';
}

.results-table .body .more-results.results-after::before {
  content: '\f101';
}

.results-table .body .more-results.results-before {
  padding-top: 12px;
  padding-bottom: 12px;
}

.results-table .body .results {
  display: flex;
  flex-flow: column nowrap;
}

.results-table .body .results .date-info-ctr {
  display: flex;
  justify-content: center;
  margin: 19px 0 1px 0;
  border-top: 1px solid #aaa;
}

.results-table .body .results .date-info {
  position: relative;
  top: -9px;
  font-size: 15px;
  font-weight: 300;
  background-color: #fff;
  padding: 0 15px;
}

.results-table .body .results .day-group:first-of-type .date-info-ctr {
  margin-top: 0px;
}

.results-table .body .results .result-item {
  display: flex;
  flex-flow: row nowrap;
  cursor: default;
}

.results-table .body .results .result-item:hover {
  background-color: #eee;
}

.results-table .body .results .result-item .departure-time span,
.results-table .body .results .result-item .arrival-time span,
.results-table .body .results .result-item .travel-time span,
.results-table .body .results .result-item .price span {
  font-size: 21px;
}

.results-table .body .results .result-item .route-info span {
  font-size: 14px;
}

.results-table .body .results .result-item .travel-time span {
  color: #777;
}
</style>
